import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { ActorPicker, useActor } from "../components/ActorPicker.tsx";
import { PrizeList, Race } from "../components/Race.tsx";
import { AlertBanner } from "../components/ui.tsx";
import { useUndo, type Undoable } from "../components/UndoToast.tsx";
import { useAsync } from "../hooks.ts";
import { latePoints } from "../../shared/points.ts";
import type { Me, Person, TaskView } from "../../shared/types.ts";

/** A wall tablet is never reloaded by hand, so it reloads itself. */
const REFRESH_MS = 60_000;
const DAY = new Intl.DateTimeFormat(undefined, { weekday: "long", day: "numeric", month: "long" });

/**
 * The kitchen display.
 *
 * A different job from Today: nobody is holding this, they are walking past
 * it. So it carries its own chrome, oversized targets, and the jars — which
 * have to be readable from across the room by someone who is four and cannot
 * yet read the names beside them.
 */
export function Board({ me }: { me: Me }) {
  const [tick, setTick] = useState(0);
  const today = useAsync(() => api.today(), [tick]);
  const scores = useAsync(() => api.scoreboard(0), [tick]);
  const people = useAsync(() => api.people());
  const { actorId, setActorId } = useActor(people.data ?? [], me.personId);
  const undo = useUndo();

  useEffect(() => {
    const timer = window.setInterval(() => setTick((value) => value + 1), REFRESH_MS);
    return () => window.clearInterval(timer);
  }, []);

  const refresh = () => setTick((value) => value + 1);

  if (today.error || scores.error) {
    return (
      <div className="kiosk">
        <AlertBanner>Couldn’t load the board. It will try again shortly.</AlertBanner>
      </div>
    );
  }
  if (!today.data || !scores.data) return <div className="kiosk kiosk--loading">Loading…</div>;

  const jobs = today.data.rooms.flatMap((room) =>
    room.tasks.map((task) => ({ task, room: room.name, emoji: room.emoji })),
  );

  return (
    <div className="kiosk">
      <header className="kiosk__head">
        <h1 className="kiosk__title">{today.data.away ? `✈️ ${today.data.tripName ?? "On a trip"}` : "Chores"}</h1>
        <span className="kiosk__day">{DAY.format(new Date())}</span>
        <div className="offer">
          <span className="kiosk__total">{scores.data.household.points}</span>
          <span className="eyebrow">points this week</span>
        </div>
      </header>

      {me.role === "parent" ? <ActorPicker people={people.data ?? []} actorId={actorId} onChange={setActorId} /> : null}

      <div className="kiosk__body">
        <section className="kiosk__jobs">
          <span className="eyebrow">Waiting</span>
          {jobs.length === 0 ? (
            <p className="board__empty">Nothing due. The house is winning.</p>
          ) : (
            <ul className="kiosk__list">
              {jobs.map(({ task, room, emoji }) => (
                <KioskJob
                  key={`${task.id}:${task.personId ?? 0}`}
                  task={task}
                  room={room}
                  emoji={emoji}
                  actorId={actorId}
                  people={people.data ?? []}
                  onDone={(entry) => {
                    undo.offer(entry);
                    refresh();
                  }}
                />
              ))}
            </ul>
          )}
          {today.data.hidden > 0 ? <p className="kiosk__hidden">+{today.data.hidden} more waiting</p> : null}
        </section>

        <aside className="kiosk__side">
          <Race title="Kids" rows={scores.data.boards.kid} markers={scores.data.markers} />
          <Race title="Grown-ups" rows={scores.data.boards.parent} markers={scores.data.markers} />
          <Race title="Staying with us" rows={scores.data.boards.guest} markers={scores.data.markers} compact />
          <PrizeList markers={scores.data.markers} rows={[...scores.data.boards.kid, ...scores.data.boards.parent]} />

          {scores.data.recent.length > 0 ? (
            <section className="board">
              <span className="eyebrow">Just done</span>
              <ul className="kiosk__recent">
                {scores.data.recent.slice(0, 5).map((entry) => (
                  <li key={entry.id} className="kiosk__recent-row">
                    <span className="kiosk__recent-text">
                      <span aria-hidden="true">{entry.emoji}</span> {entry.task}
                      <span className="muted"> — {entry.person ?? "someone"}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </aside>
      </div>
    </div>
  );
}

function KioskJob({
  task,
  room,
  emoji,
  actorId,
  people,
  onDone,
}: {
  task: TaskView;
  room: string;
  emoji: string;
  actorId: number | null;
  people: Person[];
  onDone: (entry: Undoable) => void;
}) {
  const [state, setState] = useState<"idle" | "saving" | "done" | "failed">("idle");

  async function complete() {
    if (state !== "idle") return;
    setState("saving");
    try {
      // A personal card belongs to one kid; the tick credits them, not
      // whoever's name happens to be selected on the shared tablet.
      const creditTo = task.personId ?? actorId;
      const result = await api.complete(task.id, creditTo);
      setState("done");
      const who = task.personName ?? people.find((person) => person.id === creditTo)?.name ?? null;
      // Long enough to watch the tag lift off the board.
      window.setTimeout(() => onDone({ completionId: result.completionId, what: task.title, who }), 900);
    } catch {
      setState("failed");
    }
  }

  return (
    <li>
      <button
        type="button"
        className={`kiosk__job kiosk__job--${task.status}${state === "done" ? " kiosk__job--done" : ""}`}
        onClick={complete}
        disabled={state === "saving" || state === "done"}
      >
        <span className="kiosk__job-emoji" aria-hidden="true">
          {emoji}
        </span>
        <span className="kiosk__job-text">
          <span className="kiosk__job-title">
            {task.personName ? `${task.title} — ${task.personName}` : task.title}
          </span>
          <span className="kiosk__job-room">
            {room}
            {task.daysLate > 0 ? ` · ${task.daysLate}d late` : ""}
            {task.kidFriendly ? " · kid job" : ""}
          </span>
        </span>
        {state === "failed" ? <span className="kiosk__job-error">didn’t save</span> : null}
        <span
          className={`points points--large${latePoints(task.effortMin, task.daysLate) > 0 ? " points--bounty" : ""}`}
        >
          {task.pointsLabel}
        </span>
      </button>
    </li>
  );
}
